import React, { useEffect, useState } from 'react';
import { Table, Switch, Button, Space, Modal, Form, Input, Select, Card, Tag, message, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, MinusCircleOutlined, ExperimentOutlined } from '@ant-design/icons';
import { RuleType, RoutingType } from '../types';
import { apiFetch } from '../api';

const RulesList: React.FC = () => {
  const [rules, setRules] = useState<RuleType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<RuleType | null>(null);
  const [responsibles, setResponsibles] = useState<RoutingType[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [form] = Form.useForm();

  const fetchRules = async () => {
    setLoading(true);
    try {
      const data = await apiFetch<RuleType[]>('/rules/');
      setRules(data);
    } catch (error: any) {
      message.error(error.message || 'Не удалось загрузить правила');
    }
    setLoading(false);
  };

  const fetchResponsibles = async () => {
    try {
      setResponsibles(await apiFetch<RoutingType[]>('/routing/'));
    } catch (e) { /* ignore */ }
  };

  const fetchBrands = async () => {
    try {
      const data = await apiFetch<any[]>('/brands/');
      setBrands(data.map((b) => b.name));
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchRules();
    fetchResponsibles();
    fetchBrands();
  }, []);

  const normalize = (values: any) => ({
    ...values,
    reply_templates: (values.reply_templates || []).filter((t: string) => t && t.trim()),
  });

  const handleAdd = async (values: any) => {
    try {
      await apiFetch('/rules/', { method: 'POST', body: normalize(values) });
      message.success('Правило добавлено');
      setModalVisible(false);
      fetchRules();
      form.resetFields();
    } catch (error: any) {
      message.error(error.message || 'Не удалось добавить правило');
    }
  };

  const handleEdit = async (values: any) => {
    try {
      await apiFetch(`/rules/${editingRule?.id}`, { method: 'PUT', body: normalize(values) });
      message.success('Правило обновлено');
      setModalVisible(false);
      setEditingRule(null);
      fetchRules();
      form.resetFields();
    } catch (error: any) {
      message.error(error.message || 'Не удалось обновить правило');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiFetch(`/rules/${id}`, { method: 'DELETE' });
      message.success('Правило удалено');
      fetchRules();
    } catch (error: any) {
      message.error(error.message || 'Не удалось удалить правило');
    }
  };

  const showEditModal = (rule: RuleType) => {
    setEditingRule(rule);
    // Шаблоны: используем список; если у старого правила только одиночный — оборачиваем.
    const templates = rule.reply_templates && rule.reply_templates.length
      ? rule.reply_templates
      : (rule.reply_template ? [rule.reply_template] : ['']);
    form.setFieldsValue({ ...rule, reply_templates: templates });
    setModalVisible(true);
  };

  // ===== Тест правила =====
  const [testForm] = Form.useForm();
  const [testResult, setTestResult] = useState<any | null>(null);
  const [testing, setTesting] = useState(false);

  const handleTest = async (values: any) => {
    setTesting(true);
    try {
      const res = await apiFetch<any>('/rules/test', { method: 'POST', body: values });
      setTestResult(res);
    } catch (e: any) {
      message.error(e.message || 'Не удалось проверить');
    }
    setTesting(false);
  };

  const columns = [
    { title: 'Название', dataIndex: 'name', key: 'name' },
    { title: 'Бренд', dataIndex: 'brand', key: 'brand' },
    { title: 'Тип', dataIndex: 'rule_type', key: 'rule_type', render: (type: string) => {
      const types: Record<string, string> = {
        auto_reply: 'Автоответ',
        complaint: 'Жалоба',
        manager: 'Передать менеджеру',
        negative: 'Негатив',
        ignore: 'Игнорировать'
      };
      return types[type] || type;
    }},
    { title: 'Приоритет', dataIndex: 'priority', key: 'priority' },
    {
      title: 'Режим', dataIndex: 'send_to_responsible', key: 'send_to_responsible',
      render: (v: boolean) => (v ? 'Ответственному' : 'Автоответ'),
    },
    { title: 'Активно', dataIndex: 'is_active', key: 'is_active', render: (active: boolean) => <Switch checked={active} disabled /> },
    { title: 'Действия', key: 'actions', render: (_: any, record: RuleType) => (
      <Space>
        <Button icon={<EditOutlined />} size="small" title="Редактировать" onClick={() => showEditModal(record)} />
        <Popconfirm title="Удалить правило?" okText="Удалить" cancelText="Отмена" okButtonProps={{ danger: true }} onConfirm={() => handleDelete(record.id)}>
          <Button icon={<DeleteOutlined />} size="small" danger title="Удалить" />
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <>
      <Card size="small" title={<><ExperimentOutlined /> Тест правила</>} style={{ marginBottom: 16 }}>
        <Form form={testForm} layout="inline" onFinish={handleTest}>
          <Form.Item name="brand" rules={[{ required: true, message: 'бренд' }]}>
            <Select placeholder="Бренд" style={{ width: 160 }} options={brands.map((b) => ({ value: b }))} />
          </Form.Item>
          <Form.Item name="text" rules={[{ required: true, message: 'текст' }]} style={{ flex: 1, minWidth: 280 }}>
            <Input placeholder="Текст комментария для проверки" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={testing}>Проверить</Button>
          </Form.Item>
        </Form>
        {testResult && (
          <div style={{ marginTop: 8 }}>
            {testResult.matched ? (
              <span>
                <Tag color="green">Совпало</Tag>
                Правило: <b>{testResult.rule_name}</b> ({testResult.rule_type}).{' '}
                {testResult.reply ? <>Ответ: <i>{testResult.reply}</i></> : 'Без ответа'}
              </span>
            ) : (
              <Tag color="default">Совпадений нет — уйдёт ответственному без готового ответа</Tag>
            )}
          </div>
        )}
      </Card>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingRule(null); form.resetFields(); setModalVisible(true); }} style={{ marginBottom: 16 }}>
        Добавить правило
      </Button>
      <Table columns={columns} dataSource={rules} rowKey="id" loading={loading} scroll={{ x: 'max-content' }} />
      <Modal
        title={editingRule ? "Редактировать правило" : "Новое правило"}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditingRule(null); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={editingRule ? handleEdit : handleAdd}>
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="brand" label="Бренд" rules={[{ required: true }]}>
            <Select options={brands.map((b) => ({ value: b }))} />
          </Form.Item>
          <Form.Item name="rule_type" label="Тип правила" rules={[{ required: true }]}>
            <Select options={[
              { value: 'auto_reply', label: 'Автоответ' },
              { value: 'complaint', label: 'Жалоба' },
              { value: 'manager', label: 'Передать менеджеру' },
              { value: 'negative', label: 'Негатив' },
              { value: 'ignore', label: 'Игнорировать' },
            ]} />
          </Form.Item>
          <Form.Item name="keywords" label="Ключевые слова" rules={[{ required: true }]}>
            <Input.TextArea rows={4} placeholder="Введите ключевые слова, каждое с новой строки" />
          </Form.Item>
          <Form.Item label="Шаблоны ответа (выбирается случайный — анти-бан)" tooltip="Можно добавить несколько вариантов; при ответе подставляется случайный. {brand} заменяется на название бренда.">
            <Form.List name="reply_templates" initialValue={['']}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <Form.Item {...field} noStyle>
                        <Input.TextArea rows={2} style={{ flex: 1 }} placeholder="Вариант ответа" />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(field.name)} style={{ marginTop: 8 }} />
                    </div>
                  ))}
                  <Button type="dashed" onClick={() => add('')} block icon={<PlusOutlined />}>
                    Добавить вариант
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item name="priority" label="Приоритет" initialValue={0}>
            <Input type="number" />
          </Form.Item>
          <Form.Item
            name="send_to_responsible"
            label="Отправлять ответственному на подтверждение"
            valuePropName="checked"
            initialValue={true}
            tooltip="Выкл — система сама публикует шаблонный ответ. Вкл — ответственный подтверждает/правит ответ."
          >
            <Switch />
          </Form.Item>
          <Form.Item name="responsible_ids" label="Ответственные (множественный выбор)">
            <Select
              mode="multiple"
              allowClear
              placeholder="Выберите ответственных"
              options={responsibles.map((r) => ({
                value: r.id,
                label: `@${r.telegram_username} (${r.brand} · ${r.rule_type})`,
              }))}
            />
          </Form.Item>
          <Form.Item name="is_active" label="Активно" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Сохранить</Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RulesList;